import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FiLoader, FiUsers, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { getFollowList } from "../api/profile";
import { getProfileAvatarUrl, getProfileInitial } from "../utils/userDisplay";
import useLockBackgroundScroll from "../hooks/useLockBackgroundScroll";
import FollowButton from "./FollowButton";

const LISTS = ["followers", "following"];

/**
 * A person's Followers and Following, both public. Each row opens that profile and
 * offers Follow right there.
 *
 * @param {"followers"|"following"|null} list  which tab is open; null closes the window
 * @param {() => void} onFollowsChanged  the viewer followed or unfollowed someone here
 */
const FollowListModal = ({ userId, list, onListChange, onClose, onFollowsChanged, counts, isDark = true, setToast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useLockBackgroundScroll(!!list);

  useEffect(() => {
    if (!list || !userId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    if (page === 1) setPeople([]);
    getFollowList(userId, list, page)
      .then((response) => {
        if (cancelled || !response?.success) return;
        setPeople((prev) => (page === 1 ? response.data : [...prev, ...response.data]));
        setHasMore(!!response.hasMore);
      })
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [userId, list, page]);

  // Closed: the next opening starts from the first page.
  useEffect(() => {
    if (list) return;
    setPage(1);
    setPeople([]);
    setHasMore(false);
  }, [list]);

  useEffect(() => {
    if (!list) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list, onClose]);

  if (!list) return null;

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const divider = "border-separator-default";

  const updatePerson = (id, next) => {
    setPeople((prev) => prev.map((p) => (p._id === id ? { ...p, isFollowing: next.isFollowing } : p)));
    if (next.followers !== undefined) onFollowsChanged?.();
  };

  const switchTo = (key) => {
    if (key === list) return;
    setPage(1);
    onListChange(key);
  };

  const open = (person) => {
    onClose();
    navigate(`/profile/${person._id}`);
  };

  // On the body: a blurred card around it would otherwise hold the "fixed" window inside.
  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center sm:p-4 bg-overlay-scrim"
      onClick={onClose}
      data-scroll-lock-modal
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-radius-xl border shadow-overlay sm:max-w-md sm:rounded-radius-lg ${
          "border-separator-default bg-bg-raised"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between gap-2 border-b px-2 pt-2 ${divider}`}>
          <div className="flex" role="tablist">
            {LISTS.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={list === key}
                onClick={() => switchTo(key)}
                className={`relative px-4 py-3 text-subheadline-semibold transition ${
                  list === key ? baseText : `${mutedText} ${isDark ? "hover:text-slate-200" : "hover:text-slate-700"}`
                }`}
              >
                {t(`follows.${key}`)}
                {counts?.[key] != null && <span className={`ml-1.5 tabular-nums ${mutedText}`}>{counts[key]}</span>}
                {list === key && <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-accent-default" />}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className={`mr-1 rounded-radius-xs p-2 transition hover:bg-fill-quaternary`}
          >
            <FiX className={`h-5 w-5 ${mutedText}`} />
          </button>
        </div>

        <div className="min-h-[12rem] flex-1 overflow-y-auto overscroll-contain" data-scroll-lock-target>
          {people.map((person) => (
            <div
              key={person._id}
              role="button"
              tabIndex={0}
              onClick={() => open(person)}
              onKeyDown={(e) => e.key === "Enter" && open(person)}
              className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition ${
                "hover:bg-fill-quaternary"
              }`}
            >
              {getProfileAvatarUrl(person) ? (
                <img src={getProfileAvatarUrl(person)} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-default text-subheadline-semibold text-accent-on">
                  {getProfileInitial(person)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className={`truncate text-subheadline-semibold ${baseText}`}>
                  {person.username || person.businessName || t("common.user")}
                </p>
                {person.businessName && person.username && (
                  <p className={`truncate text-footnote ${mutedText}`}>{person.businessName}</p>
                )}
              </div>
              {!person.isMe && (
                <FollowButton
                  userId={person._id}
                  isFollowing={person.isFollowing}
                  onChange={(next) => updatePerson(person._id, next)}
                  isDark={isDark}
                  size="sm"
                  setToast={setToast}
                />
              )}
            </div>
          ))}

          {loading && (
            <div className="flex justify-center py-6">
              <FiLoader className={`h-5 w-5 animate-spin ${mutedText}`} />
            </div>
          )}

          {!loading && failed && (
            <p className={`px-6 py-10 text-center text-subheadline ${mutedText}`}>{t("follows.listFailed")}</p>
          )}

          {!loading && !failed && people.length === 0 && (
            <div className="flex flex-col items-center px-6 py-12 text-center">
              <FiUsers className={`mb-3 h-8 w-8 ${mutedText} opacity-60`} />
              <p className={`text-subheadline ${mutedText}`}>
                {list === "followers" ? t("follows.noFollowers") : t("follows.noFollowing")}
              </p>
            </div>
          )}

          {!loading && hasMore && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                className="rounded-radius-sm px-4 py-2 text-subheadline-semibold text-accent-text hover:bg-accent-tinted"
              >
                {t("follows.showMore")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FollowListModal;
